import {
  AcceptUserAgreementsResultTypenames,
  FundingOption,
  PlaidEntryPointInput,
  PlaidProduct,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  getNavigationDecision,
  NavigationDecisionType,
} from 'features/advance/helpers/lightning/getNavigationDecision';
import handleEligibilityNavigation from 'features/advance/helpers/lightning/handleEligibilityNavigation';
import { getPlaidLoadingMessage } from 'features/advance/helpers/lightning/Lightning.helpers';
import useFocusedConsumerReportsAccount from 'features/advance/hooks/lightning/craUpgrade/useFocusedConsumerReportsAccount';
import useGetLightningEligibility from 'features/advance/hooks/lightning/useGetLightningEligibility';
import useInitialQueries from 'features/advance/hooks/lightning/useInitialQueries';
import useSubmitAgreement from 'features/advance/hooks/lightning/useSubmitAgreements';
import useGetAdvanceScreenContent from 'features/advance/hooks/useGetAdvanceScreenContent';
import { FlowType } from 'features/advance/screens/lightning/LinkAccount/LinkAccount.types';
import {
  AmountSelectionAlertState,
  AmountSelectionInlineAlertAtom,
} from 'features/advance/state/AmountSelectionInlineAlertAtom';
import { useFundingCallback } from 'features/onboarding/hooks/useFundingCallback';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { ONBOARDING } from 'shared/constants/onboarding';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import useEvent from 'shared/hooks/useEvent/useEvent';
import { PlaidIntent, PlaidIntentTypes } from 'shared/hooks/usePlaidV2/types';
import usePlaidV2 from 'shared/hooks/usePlaidV2/usePlaidV2';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import useVaroAdvanceFlow from 'shared/utils/useVaroAdvanceFlow/useVaroAdvanceFlow';

import {
  LinkAccountButtonConfig,
  LinkAccountFooter,
  LinkAccountScreenContent,
  LinkAccountScreenContentSection,
} from './components/LinkAccountConsumerReportSections';

const LinkAccount = () => {
  const router = useRouter();
  const [userAgreementChecked, setUserAgreementChecked] = useState(false);
  const [flowType, setFlowType] = useState<FlowType | null>(null);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [plaidSuccess, setPlaidSuccess] = useState(false);

  const [{ inlineAlertStatus }, setInlineAlertStatus] = useAtom(
    AmountSelectionInlineAlertAtom,
  );
  const { invokeCallback } = useFundingCallback(FundingOption.CashAdvance);
  const { source, skipDdPrompt } = useLocalSearchParams<{
    source?: string;
    skipDdPrompt?: string;
  }>();
  const shouldSkipDdPrompt = skipDdPrompt === 'true';

  const flow = useVaroAdvanceFlow();
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'lightningLinkAccount',
  });
  const defaultError = useDefaultError();
  const dismissToTab = useDismissToRootTab();

  const {
    isFetching,
    plaidAgreementId,
    hasError: initialQueriesHasErrors,
    hasExternalAccounts,
  } = useInitialQueries();

  const {
    data: advanceConsumerReportPermissionScreen,
    fetching: fetchingConsumerReportPermissionScreen,
    error: consumerReportPermissionScreenError,
    isConsumerReportPaused,
  } = useFocusedConsumerReportsAccount({ requestPolicy: 'cache-and-network' });

  const isFlowTypeExisting = flowType === FlowType.EXISTING;

  const canNavigateToCraUpgradeScreen =
    !!advanceConsumerReportPermissionScreen?.permissionRequired?.length &&
    isFlowTypeExisting;

  const handlePlaidSuccessEvent = () => {
    setFlowType(null);
    if (inlineAlertStatus === AmountSelectionAlertState.AWAITING_LINK) {
      setInlineAlertStatus({
        inlineAlertStatus: AmountSelectionAlertState.SUCCESSFUL_LINK,
      });
    }

    invokeCallback({ success: true });
    setPlaidSuccess(true);
  };

  const handlePlaidOnExitEvent = () => {
    setFlowType(null);
    setShowLoadingScreen(false);
    invokeCallback({ success: false });
  };

  const plaidIntent = {
    intent: PlaidIntent.Link,
    entryPoint: PlaidEntryPointInput.Advance,
    product: PlaidProduct.Auth,
  } as PlaidIntentTypes;

  const {
    openPlaid,
    error: plaidHasError,
    ready,
  } = usePlaidV2(plaidIntent, {
    sourceName: 'link_account',
  });

  const {
    result: advanceScreenContent,
    errorModel: advanceScreenContentErrorModel,
    loading: advanceScreenContentLoading,
  } = useGetAdvanceScreenContent();
  const linkScreenContent: LinkAccountScreenContent | undefined =
    advanceScreenContent?.linkingScreenContent;
  const contentBullets = linkScreenContent?.bullets ?? [];

  const {
    isAgreementAccepted,
    submitAgreement: submitAcceptedAgreement,
    fetching: fetchingSubmitAgreement,
    hasError: submitAgreementHasError,
  } = useSubmitAgreement();

  const shouldPollEligibility = Boolean(
    (isFlowTypeExisting &&
      Boolean(isAgreementAccepted) &&
      !canNavigateToCraUpgradeScreen) ||
    plaidSuccess,
  );

  const {
    fetching: fetchingEligibilityStatus,
    hasError: existingFlowHasError,
    counter,
    eligibilityStatus: status,
    data: { advanceProductsQualified },
    isFinalEligibilityStatus,
  } = useGetLightningEligibility({
    shouldPoll: shouldPollEligibility,
  });

  const flowHasError = existingFlowHasError || plaidHasError;

  const toggleUserAgreementChecked = () => {
    setUserAgreementChecked((previousState) => !previousState);
  };

  const handleOpenPlaid = useEvent(async () => {
    const result = await openPlaid();

    if (result.status === 'success') {
      handlePlaidSuccessEvent();
    } else {
      handlePlaidOnExitEvent();
    }
  });

  const submitAgreement = useCallback(
    async (shouldOpenPlaid: boolean) => {
      if (plaidAgreementId) {
        try {
          const response = await submitAcceptedAgreement({
            input: { agreementIds: [plaidAgreementId] },
          });

          const successData =
            response.data?.acceptUserAgreements.__typename ===
            AcceptUserAgreementsResultTypenames.AcceptUserAgreementsSuccess
              ? response.data.acceptUserAgreements
              : null;

          if (successData?.accepted && shouldOpenPlaid) {
            handleOpenPlaid();
          }
        } catch (e: unknown) {
          if (e instanceof AbortError) {
            return;
          }
          setShowLoadingScreen(false);
          setFlowType(null);
          logErrorWithContext(e, {
            featureArea: FeatureArea.LENDING,
            responsibleTeam: ResponsibleTeam.LENDING_CREDIT,
            severity: ErrorSeverity.HIGH,
          });
        }
      }
    },
    [handleOpenPlaid, plaidAgreementId, submitAcceptedAgreement],
  );

  const startNewAccountFlow = () => {
    if (!plaidAgreementId) {
      logErrorWithContext(
        new Error('startNewAccountFlow called without plaidAgreementId'),
        {
          featureArea: FeatureArea.LENDING,
          responsibleTeam: ResponsibleTeam.LENDING_CREDIT,
          severity: ErrorSeverity.HIGH,
        },
      );
      return;
    }
    void submitAgreement(true);
    setShowLoadingScreen(true);
    setFlowType(FlowType.NEW);
  };

  const hasError =
    initialQueriesHasErrors ||
    submitAgreementHasError ||
    flowHasError ||
    !!advanceScreenContentErrorModel ||
    consumerReportPermissionScreenError;

  const handleUseExistingAccountPress = () => {
    if (plaidAgreementId) {
      void submitAgreement(false);
      setFlowType(FlowType.EXISTING);
    } else {
      logErrorWithContext(
        new Error(
          'handleUseExistingAccountPress called without plaidAgreementId',
        ),
        {
          featureArea: FeatureArea.LENDING,
          responsibleTeam: ResponsibleTeam.LENDING_CREDIT,
          severity: ErrorSeverity.HIGH,
        },
      );
    }
  };

  const initialLoadingScreen =
    isConsumerReportPaused ||
    isFetching ||
    fetchingConsumerReportPermissionScreen;

  const isLoading =
    initialLoadingScreen ||
    fetchingEligibilityStatus ||
    showLoadingScreen ||
    advanceScreenContentLoading;

  const primaryButton: LinkAccountButtonConfig = hasExternalAccounts
    ? {
        label: translatedText('buttons.useExistingAccount'),
        isDisabled: !userAgreementChecked,
        onPress: handleUseExistingAccountPress,
        analyticsId: 'useExistingLinkedAccount',
        isLoading: fetchingSubmitAgreement,
        type: 'PRIMARY',
      }
    : {
        label: translatedText('buttons.addAnAccount'),
        isDisabled: !userAgreementChecked || !ready,
        onPress: startNewAccountFlow,
        isLoading: fetchingSubmitAgreement,
        analyticsId: 'LinkingInitiated',
        customAttributes: {
          cta: 'Link an account',
        },
        type: 'PRIMARY',
      };

  const secondaryButton: LinkAccountButtonConfig | null = hasExternalAccounts
    ? {
        label: translatedText('buttons.addADifferentAccount'),
        isDisabled: !userAgreementChecked || !ready,
        onPress: startNewAccountFlow,
        isLoading: fetchingSubmitAgreement,
        analyticsId: 'LinkingInitiated',
        customAttributes: {
          cta: 'Link a new account',
        },
        type: 'SECONDARY',
      }
    : null;

  const loadingMessage = getPlaidLoadingMessage({ counter, translatedText });

  const getLoadingAnalyticsKey = (message: string): string | null => {
    if (message === translatedText('loadingScreen.processing')) {
      return '/varo-advance/link-account/phase1';
    }
    if (message === translatedText('loadingScreen.analyzing')) {
      return '/varo-advance/link-account/phase2';
    }
    if (message === translatedText('loadingScreen.finalizing')) {
      return '/varo-advance/link-account/phase3';
    }
    return null;
  };

  const loadingAnalyticsKey = getLoadingAnalyticsKey(loadingMessage);

  const loadingAnalyticsEvent =
    !initialLoadingScreen && isLoading && loadingAnalyticsKey
      ? { key: loadingAnalyticsKey }
      : null;

  useViewDidFocusScreenAnalyticsEvent(loadingAnalyticsEvent);

  const goHome = () => {
    invokeCallback({ success: false });
    if (source === 'progress-tracker-funding') {
      return router.canGoBack()
        ? router.back()
        : router.replace('/onboarding/link-account/intro');
    }
    if (flow === ONBOARDING) {
      return router.canGoBack() ? router.back() : router.replace('/onboarding');
    }
    setFlowType(null);
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  useEffect(() => {
    const decision = getNavigationDecision({
      craUpgrade: {
        hasPermissionRequired: Boolean(
          advanceConsumerReportPermissionScreen?.permissionRequired?.length,
        ),
        shouldAttemptCraUpgrade:
          isFlowTypeExisting && Boolean(isAgreementAccepted),
      },
      eligibility: {
        hasError: Boolean(hasError),
        isFinalEligibilityStatus,
        fetchingEligibilityStatus,
        status: status ?? null,
      },
    });

    if (decision === NavigationDecisionType.CRA_UPGRADE) {
      if (shouldSkipDdPrompt) {
        router.dismissTo({
          pathname: '/varo-advance/link-account/cra-upgrade',
          params: { skipDdPrompt: 'true' },
        });
      } else {
        router.dismissTo(`/${flow}/link-account/cra-upgrade`);
      }
      return;
    }

    if (decision === NavigationDecisionType.ELIGIBILITY) {
      handleEligibilityNavigation({
        status: status ?? null,
        advanceProductsQualified,
        flow,
        skipDdPrompt: shouldSkipDdPrompt,
      });
    }
  }, [
    advanceConsumerReportPermissionScreen?.permissionRequired?.length,
    advanceProductsQualified,
    fetchingEligibilityStatus,
    flow,
    hasError,
    isAgreementAccepted,
    isFinalEligibilityStatus,
    isFlowTypeExisting,
    router,
    shouldSkipDdPrompt,
    status,
  ]);

  return (
    <View style={styles.container} testID="link-account-v2">
      <Stack.Screen
        options={{
          headerBackVisible: !isLoading,
          headerShown: true,
          gestureEnabled: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={!isLoading ? <Icon iconKey="BACK" /> : null}
              primaryOnPress={goHome}
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={isLoading && !hasError}
        LoadingComponent={
          initialLoadingScreen ? (
            <LoadingSpinner />
          ) : (
            <FullScreenLoading
              isCoinLoader
              titleText={loadingMessage}
              bodyText={translatedText('loadingScreen.body')}
            />
          )
        }
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenAnalyticsEventKey="/varo-advance/link-account/error"
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultError.title}
              messageBody={defaultError.message}
              primaryButton={{
                title: translatedText('errorButton'),
                onPress: () => dismissToTab(Tabs.HOME),
              }}
            />
          ) : null
        }
      />

      <LinkAccountScreenContentSection
        contentBullets={contentBullets}
        onToggleAgreement={toggleUserAgreementChecked}
        screenContent={linkScreenContent}
        userAgreementChecked={userAgreementChecked}
      />
      <LinkAccountFooter
        bottomInset={bottom}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default LinkAccount;
